/**
 * Pagina di dettaglio del titolo — `/instruments/:instrumentId` (SPEC-016, F-067).
 *
 * L'unico posto dove un titolo esiste di per sé, indipendentemente da
 * watchlist, portafoglio o wishlist. Una sola chiamata aggregata per tutto
 * tranne la serie del grafico; ogni sezione può essere `null` e la pagina lo
 * dichiara — un dato assente non vale zero, né qui né nel grafico.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatPercent,
  formatQuantity,
  INSTRUMENT_TYPE_LABELS,
  labelFor,
  PORTFOLIO_TYPE_LABELS,
  signOf,
  TX_TYPE_LABELS,
} from '@/lib/format';
import { Alert, Badge, Button, Card, DomainBadge, EmptyState, Spinner, StatCard, Switch, TableWrap } from '@/ui';
import type { MarketStatus } from '@/features/markets/api';
import { formatCountdown, nextEventFor, STATUS_LABELS, STATUS_TONE } from '@/features/markets/market-status-format';
import { RATING_HINT, RATING_TONE } from '@/features/watchlist/analysis-rating';
import { useRequestAnalysis } from '@/features/watchlist/ai-analysis-api';
import { needsTickerMatch } from '@/features/watchlist/mapping-api';
import { InstrumentOutcomesCard } from '@/features/outcomes/InstrumentOutcomesCard';
import { TagList } from '@/features/tags/TagList';
import { TagsModal } from '@/features/tags/TagsModal';
import {
  downloadHistoryCsv,
  useInstrumentDetail,
  useInstrumentSeries,
  useRefreshIntraday,
  type InstrumentDetail,
  type SeriesRange,
} from './api';
import { BackfillDialog } from './BackfillDialog';
import { CommercialName } from './CommercialName';
import { DataProvenance } from './DataProvenance';
import { buildExternalLinks } from './external-links';
import { IdentifiersTable } from './IdentifiersTable';
import { AddToWatchlistButton, AddToWishlistButton } from './ListMembershipActions';
import { PriceChart, type PriceChartMode } from './PriceChart';
import './instruments.css';
import { InstrumentLogo } from './InstrumentLogo';

const RANGES: Array<{ value: SeriesRange; label: string }> = [
  { value: '1d', label: '1G' },
  { value: '5d', label: '5G' },
  { value: '7d', label: '7G' },
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '6m', label: '6M' },
  { value: 'ytd', label: 'YTD' },
  { value: '1y', label: '1A' },
  { value: '5y', label: '5A' },
  { value: 'max', label: 'MAX' },
];

const ORIGIN_LABELS: Record<string, string> = {
  import: 'Import',
  manual: 'Registrazione manuale',
  broker_sync: 'Sync API broker',
  ai_analysis: 'Da analisi AI',
};

/** Colori serie per fonte, letti dai token a runtime (mai hardcodati, ADR-0005). */
function tokenValue(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function useSourceColors(sources: string[]): Record<string, string> {
  return useMemo(() => {
    const palette = ['--color-cat-1', '--color-cat-4', '--color-cat-2', '--color-cat-6', '--color-cat-5', '--color-cat-3'];
    const colors: Record<string, string> = {};
    sources.forEach((source, index) => {
      colors[source] = tokenValue(palette[index % palette.length] ?? '--color-cat-1');
    });
    return colors;
  }, [sources]);
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

export function InstrumentDetailPage() {
  const { instrumentId } = useParams<{ instrumentId: string }>();
  const detail = useInstrumentDetail(instrumentId ?? '');

  if (detail.isLoading) return <Spinner label="Caricamento del titolo…" />;
  if (detail.isError) {
    const notFound = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <EmptyState
        icon="🔍"
        title={notFound ? 'Titolo non trovato' : 'Titolo non disponibile'}
        description={
          notFound
            ? 'Questo strumento non esiste o non è fra i tuoi: posseduto, con movimenti, in watchlist o in wishlist.'
            : errorMessage(detail.error)
        }
        action={
          <Link className="btn btn--secondary" to="/watchlists">
            Vai alla watchlist
          </Link>
        }
      />
    );
  }
  if (!detail.data || !instrumentId) return null;

  return <DetailBody instrumentId={instrumentId} detail={detail.data} />;
}

function DetailBody({ instrumentId, detail }: { instrumentId: string; detail: InstrumentDetail }) {
  const { identity, mapping, market, quote, positions, watchlist, wishlist, analyses } = detail;

  const [range, setRange] = useState<SeriesRange>('3m');
  const [mode, setMode] = useState<PriceChartMode>('candles');
  const [showVolume, setShowVolume] = useState(true);
  const [logScale, setLogScale] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Gli unici due orizzonti in cui una chiusura giornaliera non direbbe nulla:
  // «1G» sarebbe un punto solo, «7G» sette. Da 1M in su l'EOD è il dato giusto.
  const granularity = range === '1d' || range === '7d' ? 'intraday' : 'daily';
  const series = useInstrumentSeries(instrumentId, range, granularity);
  const refreshIntraday = useRefreshIntraday(instrumentId);

  // Le barre orarie ci sono solo se qualcuno le ha scaricate: senza questo, la
  // prima apertura di «7G» mostrerebbe un grafico vuoto e nessun indizio sul
  // perché. Parte una volta sola per montaggio della pagina — il TTL e la
  // quota lato server sono la difesa vera, questo ref evita di bussare a ogni
  // ritorno sull'orizzonte.
  const intradayRequested = useRef(false);
  const seriesPointCount = series.data?.points.length ?? 0;
  useEffect(() => {
    if (range !== '7d' || series.isLoading || intradayRequested.current) return;
    if (seriesPointCount > 0) return;
    intradayRequested.current = true;
    refreshIntraday.mutate();
    // `refreshIntraday` è stabile fra i render: includerlo rifarebbe partire
    // l'effetto a ogni cambio di stato della mutazione, cioè durante se stessa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, series.isLoading, seriesPointCount]);
  const seriesSources = useMemo(
    () => [...new Set((series.data?.points ?? []).map((point) => point.source))],
    [series.data],
  );
  // Basta un punto con l'apertura per sapere che la serie è fatta di barre e
  // non di catture: le due sorgenti intraday non si mescolano mai (lato server).
  const hasOhlc = (series.data?.points ?? []).some((point) => point.open !== null);
  const coverageSources = useMemo(
    () => [...new Set([...seriesSources, ...(detail.coverage ?? []).map((entry) => entry.source)])],
    [seriesSources, detail.coverage],
  );
  const sourceColors = useSourceColors(coverageSources);

  // Il pulsante di aggiunta compare solo dove la riga NON c'è già; una
  // candidatura scaduta non conta come presenza, altrimenti un titolo uscito
  // dalla wishlist mesi fa non potrebbe più rientrarci da qui.
  const hasActiveWishlist = (wishlist ?? []).some((entry) => !entry.expired);
  const tickerMissing = needsTickerMatch(mapping);
  const listInstrument = { id: instrumentId, name: identity.name, symbol: mapping.providerSymbol ?? identity.symbol };

  const openPositions = (positions?.items ?? []).filter((item) => item.open);
  const totalQuantity = openPositions.length > 0 ? openPositions.reduce((sum, item) => sum + Number(item.quantity ?? 0), 0) : null;

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadHistoryCsv(instrumentId, `${identity.symbol ?? identity.isin ?? instrumentId}-storico.csv`);
    } finally {
      setExporting(false);
    }
  };

  const externalLinks = buildExternalLinks({
    providerSymbol: mapping.providerSymbol,
    isin: identity.isin,
    marketMic: mapping.marketMic,
  });

  return (
    <div className="idetail">
      <nav className="idetail__breadcrumb" aria-label="Percorso">
        <Link to="/watchlists">Watchlist</Link>
        {market && (
          <>
            <span aria-hidden="true"> / </span>
            <Link to={`/markets/${market.mic}`}>{market.name}</Link>
          </>
        )}
        <span aria-hidden="true"> / </span>
        <span>{identity.name}</span>
      </nav>

      {/* ------------------------------------------------ Header */}
      <header className="idetail__header">
        <div className="idetail__identity">
          <div className="idetail__title-row">
            <InstrumentLogo instrumentId={identity.id} name={identity.name} size="md" />
            <h1>{identity.name}</h1>
            {mapping.providerSymbol && <Badge tone="accent">{mapping.providerSymbol}</Badge>}
            <Badge>{labelFor(INSTRUMENT_TYPE_LABELS, identity.instrumentType)}</Badge>
            {/* SPEC-026 §2.7: l'ambito accanto al tipo, perché il tipo da solo
                non lo dice — un ETN su Bitcoin è «crypto» come tipo e «Borsa»
                come ambito. */}
            <DomainBadge domain={identity.domain} />
            {/* F-147: per una crypto il rank e la regola delle top 200, dal
                registro. «Non misurato» e' un esito, non un dato mancante. */}
            {identity.crypto && (
              <Badge
                tone={identity.crypto.investable.ok ? 'neutral' : 'warning'}
                title={
                  identity.crypto.investable.ok
                    ? `Posizione per capitalizzazione nella classifica letta il ${formatDate(identity.crypto.rankAsOf)}: entro le prime 200, quindi ammessa alle operazioni automatiche.`
                    : `Regola delle prime 200 per capitalizzazione: ${identity.crypto.investable.reason}. Le operazioni automatiche di acquisto non la propongono; vendere resta possibile.`
                }
              >
                {identity.crypto.marketCapRank === null ? 'cap. non misurata' : `#${identity.crypto.marketCapRank} per cap.`}
              </Badge>
            )}
          </div>
          {/* F-087: sotto il titolo e sopra gli identificativi. `name` dice
              quale riga è, questo dice che società è. */}
          <CommercialName name={identity.name} commercialName={identity.commercialName} />

          {/* ⚠️ Non e' un dato mancante, e' un dato affermato con sicurezza:
              tutti gli altri avvisi di questa pagina segnalano assenze, e per
              questo il caso `ECO` non aveva modo di farsi notare. Sta accanto
              all'identita' e non in fondo perche' mette in dubbio proprio
              quella. */}
          {identity.providerNameMismatch && (
            <Alert variant="warning" title="Il provider parla di un'altra società">
              A catalogo questo titolo si chiama <strong>{identity.name}</strong>, ma il provider
              chiama <code>{mapping.providerSymbol}</code>{' '}
              <strong>{identity.commercialName}</strong>: i due nomi non hanno nulla in comune.
              Se il simbolo è sbagliato, con lui sono sbagliati anche prezzo, valuta e serie
              storica. Verifica il ticker dalla tabella degli identificativi qui sotto.
            </Alert>
          )}
          <p className="idetail__subtitle numeric">
            {identity.isin ?? 'Senza ISIN'}
            {market && <> · {market.name} {market.mic}</>}
            <> · Valuta {identity.currency}</>
            {identity.assetClass && <> · {identity.assetClass}</>}
          </p>
          <div className="idetail__badges">
            {openPositions.length > 0 && (
              <Badge tone="positive">
                In portafoglio · {formatQuantity(totalQuantity)} pz
              </Badge>
            )}
            {watchlist && <Badge tone="accent">In watchlist dal {formatDate(watchlist.addedAt)}</Badge>}
            {wishlist?.some((entry) => !entry.expired) && (
              <Badge tone="warning">
                In wishlist · scade fra {Math.max(...wishlist.filter((e) => !e.expired).map((e) => e.daysRemaining))} giorni
              </Badge>
            )}
            {identity.trading212Ticker && <Badge>Collegato a Trading 212</Badge>}
          </div>
        </div>

        <div className="idetail__quote">
          {quote ? (
            <>
              <span className="idetail__price numeric">
                {formatCurrency(quote.price, quote.currency)}
              </span>
              <span className={`idetail__delta text-${signOf(quote.changeVsPreviousClosePct)}`}>
                {formatPercent(quote.changeVsPreviousClosePct)}{' '}
                <span className="idetail__delta-hint">
                  {quote.kind === 'intraday' ? 'oggi' : 'ultima seduta'}
                </span>
              </span>
              {market && (
                <MarketStatusLine market={market} />
              )}
              <span className="idetail__provenance">
                {formatDateTime(quote.capturedAt)} · fonte {quote.source}{' '}
                <code className="idetail__table-ref">{quote.table}</code>
              </span>
            </>
          ) : (
            <span className="idetail__provenance">
              Nessun prezzo noto: la fonte e il timestamp compaiono qui appena esiste un dato.
            </span>
          )}
        </div>
      </header>

      {/* ------------------------------------------------ Riga stat */}
      <div className="idetail__stats">
        <StatCard
          label="Ultimo prezzo"
          value={quote ? formatCurrency(quote.price, quote.currency) : '—'}
          hint={quote ? `${formatPercent(quote.changeVsPreviousClosePct)} vs chiusura` : 'non disponibile'}
        />
        <StatCard
          label="Var. 5 giorni"
          value={quote?.change5dPct !== null && quote ? formatPercent(quote.change5dPct) : '—'}
          tone={quote ? (signOf(quote.change5dPct) === 'negative' ? 'negative' : signOf(quote.change5dPct) === 'positive' ? 'positive' : 'neutral') : 'neutral'}
          hint={quote?.min5d ? `min ${quote.min5d} · max ${quote.max5d}` : undefined}
        />
        <StatCard
          label="Da watchlist"
          value={watchlist?.deltaPct != null ? formatPercent(watchlist.deltaPct) : '—'}
          tone={watchlist?.deltaPct != null && signOf(watchlist.deltaPct) === 'positive' ? 'positive' : watchlist?.deltaPct != null && signOf(watchlist.deltaPct) === 'negative' ? 'negative' : 'neutral'}
          hint={watchlist?.addedPrice ? `${formatCurrency(watchlist.addedPrice, watchlist.addedPriceCurrency ?? identity.currency)} · ${formatDate(watchlist.addedAt)}` : 'non in watchlist'}
        />
        <StatCard
          label="Posizione"
          value={positions?.totalMarketValue ? formatCurrency(positions.totalMarketValue, openPositions[0]?.currency ?? identity.currency) : positions?.incompleteValuation ? 'incompleta' : '—'}
          hint={openPositions.length > 0 ? `${formatQuantity(totalQuantity)} pz` : 'nessuna posizione aperta'}
        />
        <StatCard
          label="P/L non realizzata"
          value={positions?.totalUnrealizedPl ? formatCurrency(positions.totalUnrealizedPl, openPositions[0]?.currency ?? identity.currency) : '—'}
          tone={positions?.totalUnrealizedPl ? (signOf(positions.totalUnrealizedPl) === 'negative' ? 'negative' : 'positive') : 'neutral'}
        />
        <StatCard
          label="Rating AI"
          value={
            analyses?.latest?.rating ? (
              <Badge tone={RATING_TONE[analyses.latest.rating]} title={RATING_HINT[analyses.latest.rating] ?? undefined}>{analyses.latest.rating}</Badge>
            ) : (
              '—'
            )
          }
          hint={analyses?.latest ? formatDate(analyses.latest.completedAt) : 'nessuna analisi'}
        />
      </div>

      <div className="idetail__grid">
        <div className="idetail__main">
          {/* ------------------------------------------- Grafico */}
          <Card
            title="Andamento del prezzo"
            action={
              <div className="idetail__chart-controls">
                <div className="idetail__ranges" role="group" aria-label="Orizzonte temporale">
                  {RANGES.map((entry) => (
                    <button
                      key={entry.value}
                      type="button"
                      className={entry.value === range ? 'range-btn range-btn--active' : 'range-btn'}
                      onClick={() => setRange(entry.value)}
                    >
                      {entry.label}
                    </button>
                  ))}
                </div>
                <div className="idetail__ranges" role="group" aria-label="Tipo di grafico">
                  <button
                    type="button"
                    className={mode === 'line' ? 'range-btn range-btn--active' : 'range-btn'}
                    onClick={() => setMode('line')}
                  >
                    Linea
                  </button>
                  <button
                    type="button"
                    className={mode === 'candles' ? 'range-btn range-btn--active' : 'range-btn'}
                    onClick={() => setMode('candles')}
                  >
                    Candele
                  </button>
                </div>
                <Switch label="Volumi" checked={showVolume} onChange={setShowVolume} />
                <Switch label="Scala log" checked={logScale} onChange={setLogScale} />
                {range === '7d' && (
                  <Button
                    variant="ghost"
                    loading={refreshIntraday.isPending}
                    onClick={() => refreshIntraday.mutate()}
                    title="Riscarica le barre orarie degli ultimi giorni"
                  >
                    Aggiorna dettaglio orario
                  </Button>
                )}
              </div>
            }
          >
            {series.isLoading && <Spinner label="Caricamento della serie…" />}
            {series.isError && (
              <Alert variant="error" title="Serie non disponibile">
                {errorMessage(series.error)}
              </Alert>
            )}
            {range === '7d' && refreshIntraday.isPending && (
              <Spinner label="Scarico il dettaglio orario…" />
            )}
            {range === '7d' && refreshIntraday.isError && (
              <Alert variant="warning" title="Dettaglio orario non scaricato">
                {errorMessage(refreshIntraday.error)}
              </Alert>
            )}
            {range === '7d' && !refreshIntraday.isPending && seriesPointCount === 0 && !series.isLoading && (
              <p className="idetail__muted">
                Nessuna barra oraria per questo titolo nella finestra.{' '}
                <button type="button" className="watchlist__mapping-resolve" onClick={() => refreshIntraday.mutate()}>
                  Riprova a scaricarla
                </button>
              </p>
            )}
            {series.data && (
              <PriceChart
                points={series.data.points}
                gaps={series.data.gaps}
                granularity={series.data.granularity}
                // Non è la granularità a decidere, ma il DATO: gli snapshot
                // intraday hanno solo la chiusura e come candele sarebbero
                // trattini, le barre orarie hanno OHLC e volume come una
                // seduta. Legare la scelta alla granularità avrebbe negato le
                // candele proprio ai punti che possono disegnarle.
                mode={hasOhlc ? mode : 'line'}
                showVolume={showVolume && hasOhlc}
                logScale={logScale}
                sourceColors={sourceColors}
              />
            )}
            <DataProvenance
              coverage={detail.coverage}
              gaps={series.data?.gaps ?? []}
              sourceColors={sourceColors}
              onBackfill={() => setBackfillOpen(true)}
              onExport={() => void exportCsv()}
              exporting={exporting}
            />
          </Card>

          {/* ------------------------------------------- Identificativi */}
          <Card title="Identificativi e ticker collegati">
            <IdentifiersTable detail={detail} />
          </Card>

          {/* ------------------------------------------- Posizione e movimenti */}
          <Card title="Posizione e movimenti">
            {!positions && <p className="idetail__muted">Sezione non disponibile in questo momento.</p>}
            {positions && positions.items.length === 0 && (
              <p className="idetail__muted">Nessun portafoglio ha questo titolo.</p>
            )}
            {positions && positions.items.length > 0 && (
              <>
                <div className="idetail__positions">
                  {positions.items.map((position) => (
                    <div key={position.portfolio.id} className="position-card">
                      <div className="position-card__head">
                        <Link to={`/portfolios/${position.portfolio.id}`}>
                          <strong>{position.portfolio.name}</strong>
                        </Link>
                        <Badge tone={position.open ? 'positive' : 'neutral'}>
                          {position.open ? 'aperta' : 'chiusa'}
                        </Badge>
                        {position.portfolio.connectedProvider && <Badge tone="accent">API broker</Badge>}
                        <span className="idetail__muted">
                          {labelFor(PORTFOLIO_TYPE_LABELS, position.portfolio.portfolioType)}
                        </span>
                      </div>
                      <dl className="position-card__facts">
                        <dt>Quantità</dt>
                        <dd className="numeric">{formatQuantity(position.quantity)}</dd>
                        <dt>Carico medio</dt>
                        <dd className="numeric">{formatCurrency(position.avgBuyPrice, position.currency)}</dd>
                        <dt>Controvalore</dt>
                        <dd className="numeric">{formatCurrency(position.marketValue, position.currency)}</dd>
                        <dt>P/L</dt>
                        <dd className={`numeric text-${signOf(position.unrealizedPl)}`}>
                          {formatCurrency(position.unrealizedPl, position.currency)}
                          {position.unrealizedPlPct && <> ({formatPercent(position.unrealizedPlPct)})</>}
                        </dd>
                        {position.weightPct && (
                          <>
                            <dt>Peso</dt>
                            <dd className="numeric">{formatPercent(position.weightPct, 2, { signed: false })}</dd>
                          </>
                        )}
                      </dl>
                      {position.firstBuyDate && (
                        <p className="idetail__muted">
                          Primo acquisto {formatDate(position.firstBuyDate)}
                          {position.snapshotDate && <> · snapshot del {formatDate(position.snapshotDate)}</>}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <TableWrap>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th className="numeric">Qtà</th>
                        <th className="numeric">Prezzo</th>
                        <th className="numeric">Importo</th>
                        <th>Portafoglio</th>
                        <th>Origine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.transactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td className="numeric">{formatDate(transaction.txDate)}</td>
                          <td>{labelFor(TX_TYPE_LABELS, transaction.txType)}</td>
                          <td className="numeric">{formatQuantity(transaction.quantity)}</td>
                          <td className="numeric">{formatCurrency(transaction.price, transaction.currency)}</td>
                          <td className={`numeric text-${signOf(transaction.amount)}`}>
                            {formatCurrency(transaction.amount, transaction.currency)}
                          </td>
                          <td>
                            <Link to={`/portfolios/${transaction.portfolio.id}`}>{transaction.portfolio.name}</Link>
                          </td>
                          <td>
                            {transaction.origin === 'ai_analysis' && transaction.sourceAnalysisId ? (
                              <Link to={`/watchlists/analyses/${instrumentId}/${transaction.sourceAnalysisId}`}>
                                {ORIGIN_LABELS[transaction.origin]}
                              </Link>
                            ) : (
                              ORIGIN_LABELS[transaction.origin]
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableWrap>
                {positions.transactionsTotal > positions.transactions.length && (
                  <p className="idetail__muted">
                    Mostrati gli ultimi {positions.transactions.length} movimenti su {positions.transactionsTotal}: il
                    registro completo è nel dettaglio del portafoglio.
                  </p>
                )}
              </>
            )}
          </Card>

          {/* ------------------------------- Esiti dei giudizi passati (F-126)
              Sta PRIMA del briefing AI di proposito: e' il contesto con cui
              leggere il prossimo giudizio, non un commento a valle. La scheda
              non si mostra affatto quando non c'e' niente da dire. */}
          <InstrumentOutcomesCard instrumentId={instrumentId} />

          {/* ------------------------------------------- Briefing AI */}
          <Card title="Briefing AI del titolo" action={<NewAnalysisButton instrumentId={instrumentId} enabled={analyses !== null} />}>
            {!analyses && (
              <p className="idetail__muted">
                Analisi AI non disponibili: il servizio è disattivato o momentaneamente irraggiungibile.
              </p>
            )}
            {analyses && !analyses.latest && (
              <p className="idetail__muted">Nessuna analisi completata per questo titolo.</p>
            )}
            {analyses?.latest && (
              <div className="idetail__analysis">
                <div className="idetail__analysis-head">
                  {analyses.latest.rating && (
                    <Badge tone={RATING_TONE[analyses.latest.rating]} title={RATING_HINT[analyses.latest.rating] ?? undefined}>{analyses.latest.rating}</Badge>
                  )}
                  <span>Analisi del {formatDate(analyses.latest.completedAt ?? analyses.latest.requestedAt)}</span>
                  {analyses.latest.priceTarget !== null && (
                    <span className="numeric">
                      Obiettivo {formatCurrency(String(analyses.latest.priceTarget), identity.currency)}
                    </span>
                  )}
                  {analyses.latest.timeHorizon && <span>Orizzonte {analyses.latest.timeHorizon}</span>}
                  <Badge tone={analyses.latest.triggeredBy === 'manual' ? 'neutral' : 'accent'}>
                    {analyses.latest.triggeredBy === 'manual' ? 'Manuale' : 'Trigger di mercato'}
                  </Badge>
                </div>
                {analyses.latest.executiveSummary && <p>{analyses.latest.executiveSummary}</p>}
                <p>
                  <Link to={`/watchlists/analyses/${instrumentId}/${analyses.latest.id}`}>
                    Apri il report completo →
                  </Link>
                </p>
              </div>
            )}
            {analyses && analyses.recent.length > 0 && (
              <TableWrap>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Rating</th>
                      <th className="numeric">Obiettivo</th>
                      <th>Origine</th>
                      <th>Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyses.recent.map((analysis) => (
                      <tr key={analysis.id}>
                        <td className="numeric">
                          <Link to={`/watchlists/analyses/${instrumentId}/${analysis.id}`}>
                            {formatDate(analysis.tradeDate)}
                          </Link>
                        </td>
                        <td>
                          {analysis.rating ? (
                            <Badge tone={RATING_TONE[analysis.rating]} title={RATING_HINT[analysis.rating] ?? undefined}>{analysis.rating}</Badge>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="numeric">
                          {analysis.priceTarget !== null
                            ? formatCurrency(String(analysis.priceTarget), identity.currency)
                            : '—'}
                        </td>
                        <td>{analysis.triggeredBy === 'manual' ? 'Manuale' : 'Trigger di mercato'}</td>
                        <td>{analysis.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            )}
            {analyses && (
              <p className="idetail__muted">
                Quota manuale oggi: {analyses.quota.used} / {analyses.quota.limit}
              </p>
            )}
          </Card>
        </div>

        {/* ------------------------------------------- Colonna destra */}
        <aside className="idetail__aside">
          <Card
            title="Watchlist"
            action={
              watchlist ? undefined : (
                <AddToWatchlistButton instrument={listInstrument} needsTicker={tickerMissing} />
              )
            }
          >
            {!watchlist && (
              <p className="idetail__muted">Questo titolo non è nella tua watchlist.</p>
            )}
            {watchlist && (
              <dl className="idetail__facts">
                <dt>Prezzo congelato all’aggiunta</dt>
                <dd className="numeric">
                  {formatCurrency(watchlist.addedPrice, watchlist.addedPriceCurrency ?? identity.currency)}
                </dd>
                <dt>Variazione da allora</dt>
                <dd className={`numeric text-${signOf(watchlist.deltaPct)}`}>{formatPercent(watchlist.deltaPct)}</dd>
                {watchlist.targetPrice && (
                  <>
                    <dt>Tuo prezzo obiettivo</dt>
                    <dd className="numeric">{formatCurrency(watchlist.targetPrice, identity.currency)}</dd>
                  </>
                )}
                <dt>Aggiunto il</dt>
                <dd className="numeric">{formatDate(watchlist.addedAt)}</dd>
                {watchlist.notes && (
                  <>
                    <dt>Note</dt>
                    <dd>{watchlist.notes}</dd>
                  </>
                )}
              </dl>
            )}
            <p>
              <Link to="/watchlists">Apri la watchlist →</Link>
            </p>
          </Card>

          <Card
            title="Wishlist — candidato all’acquisto"
            action={
              hasActiveWishlist ? undefined : (
                <AddToWishlistButton instrument={listInstrument} needsTicker={tickerMissing} />
              )
            }
          >
            {!wishlist && <p className="idetail__muted">Nessuna candidatura all’acquisto per questo titolo.</p>}
            {wishlist?.map((entry) => (
              <dl key={entry.id} className="idetail__facts">
                <dt>Portafoglio di destinazione</dt>
                <dd>
                  <Link to={`/portfolios/${entry.portfolio.id}`}>{entry.portfolio.name}</Link>
                </dd>
                {entry.maxExposureAmount && (
                  <>
                    <dt>Esposizione massima</dt>
                    <dd className="numeric">{formatCurrency(entry.maxExposureAmount, entry.maxExposureCurrency)}</dd>
                  </>
                )}
                {entry.maxPortfolioPercentage && (
                  <>
                    <dt>Peso massimo sul portafoglio</dt>
                    <dd className="numeric">{formatPercent(entry.maxPortfolioPercentage)}</dd>
                  </>
                )}
                <dt>Scadenza</dt>
                <dd className="numeric">
                  {formatDate(entry.expiresAt)}{' '}
                  {entry.expired ? <Badge tone="negative">scaduta</Badge> : <Badge tone="warning">{entry.daysRemaining} gg</Badge>}
                </dd>
              </dl>
            ))}
            <p>
              <Link to="/wishlist">Apri la wishlist →</Link>
            </p>
          </Card>

          <TagsCard
            instrumentId={instrumentId}
            instrumentName={identity.name}
            tags={detail.tags}
          />

          <NewsCard news={detail.news} />

          <Card title="Mercato di quotazione">
            {!market && (
              <p className="idetail__muted">
                Mercato non risolto per questo titolo: lo stato di seduta non è disponibile.
              </p>
            )}
            {market && (
              <dl className="idetail__facts">
                <dt>Borsa</dt>
                <dd>
                  <Link to={`/markets/${market.mic}`}>
                    {market.name} <span className="numeric">{market.mic}</span>
                  </Link>
                </dd>
                <dt>Stato</dt>
                <dd>
                  <Badge tone={STATUS_TONE[market.status as MarketStatus] ?? 'neutral'}>
                    {STATUS_LABELS[market.status as MarketStatus] ?? market.status}
                  </Badge>
                  {market.trades24x7 && <> Sempre aperto</>}
                </dd>
                {!market.trades24x7 && (
                  <>
                    <dt>Sessione</dt>
                    <dd className="numeric">
                      {market.continuousOpen} – {market.continuousClose} ({market.timezoneIana})
                    </dd>
                  </>
                )}
                <MarketCountdownFacts market={market} />
                <dt>Trigger pre-apertura</dt>
                <dd>{market.triggerEnabled ? 'attivo' : 'non attivo'}</dd>
                {market.latestBriefing && (
                  <>
                    <dt>Ultimo resoconto</dt>
                    <dd>
                      <Link to={`/markets/${market.mic}/briefings/${market.latestBriefing.id}`}>
                        Briefing del {formatDate(market.latestBriefing.briefingDate)} →
                      </Link>
                    </dd>
                  </>
                )}
              </dl>
            )}
          </Card>

          <Card title="Apri su fonti esterne">
            {externalLinks.links.length === 0 && (
              <p className="idetail__muted">Nessun collegamento disponibile senza ticker né ISIN.</p>
            )}
            <ul className="idetail__links">
              {externalLinks.links.map((link) => (
                <li key={link.url}>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.label}
                  </a>{' '}
                  <span className="idetail__muted">{link.host} ↗</span>
                </li>
              ))}
            </ul>
            <p className="idetail__muted">
              {externalLinks.fallbackOnly
                ? 'Il mercato di questo titolo non ha una scheda pubblica in tabella: resta il solo Yahoo Finance.'
                : 'I collegamenti ufficiali dipendono dal MIC risolto; mai un URL indovinato.'}
            </p>
          </Card>

          <Card title="Catena di quotazione">
            {!detail.providers && <p className="idetail__muted">Stato dei provider non disponibile.</p>}
            {detail.providers && (
              <ul className="idetail__providers">
                {detail.providers.map((provider, index) => (
                  <li key={provider.provider} className={provider.enabled ? '' : 'idetail__provider--off'}>
                    <span className="idetail__provider-name">
                      {provider.provider}
                      <span className="idetail__muted"> {provider.role === 'primary' ? 'primario' : `fallback ${index}`}</span>
                    </span>
                    <span className="numeric">
                      {!provider.enabled
                        ? 'disattivo'
                        : provider.limit !== null
                          ? `${provider.used ?? 0} / ${provider.limit} ${provider.window === 'month' ? 'mese' : 'oggi'}`
                          : 'senza tetto'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </aside>
      </div>

      {backfillOpen && <BackfillDialog instrumentId={instrumentId} onClose={() => setBackfillOpen(false)} />}
    </div>
  );
}

/**
 * Tag del titolo (F-079).
 *
 * I chip portano alla vista del tag; il pulsante apre il modal condiviso, che è
 * lo stesso richiamato dalle altre sezioni. La sezione `null` è dichiarata come
 * tutte le altre della pagina — un elenco vuoto significa «nessun tag», che è
 * una cosa diversa da «non ho potuto leggerli».
 */
function TagsCard({
  instrumentId,
  instrumentName,
  tags,
}: {
  instrumentId: string;
  instrumentName: string;
  tags: string[] | null;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Card
      title="Tag"
      action={
        <Button variant="ghost" onClick={() => setOpen(true)} disabled={tags === null}>
          {tags && tags.length > 0 ? 'Modifica' : 'Aggiungi'}
        </Button>
      }
    >
      {tags === null && <p className="idetail__muted">Sezione non disponibile in questo momento.</p>}
      {tags?.length === 0 && (
        <p className="idetail__muted">
          Nessun tag. Servono a ritrovare i titoli per tema: «ai», «energia», «italia».
        </p>
      )}
      {tags && tags.length > 0 && <TagList tags={tags} />}

      {open && (
        <TagsModal
          instrumentId={instrumentId}
          instrumentName={instrumentName}
          currentTags={tags ?? []}
          onClose={() => setOpen(false)}
        />
      )}
    </Card>
  );
}

/**
 * Notizie recenti sul titolo (F-074).
 *
 * I tre stati sono distinti perché dicono cose diverse, e appiattirli in
 * «nessuna notizia» sarebbe una bugia in due casi su tre: la sezione può non
 * essere disponibile (errore, come ogni altra sezione della pagina), la
 * raccolta può essere **spenta** — e allora nessuno sta cercando notizie, il
 * che non è la stessa cosa che non trovarne — oppure può essere accesa e non
 * aver trovato nulla, che per un titolo europeo è il caso normale e non un
 * guasto.
 *
 * I titoli aprono l'articolo originale in una scheda nuova con `noreferrer`,
 * come gli altri collegamenti esterni della pagina: sono URL di terze parti, e
 * l'unica provenienza ammessa è quella del provider — nessun indirizzo
 * costruito da noi.
 */
function NewsCard({ news }: { news: InstrumentDetail['news'] }) {
  return (
    <Card title="Notizie recenti">
      {!news && <p className="idetail__muted">Sezione non disponibile in questo momento.</p>}

      {news && news.items.length === 0 && (
        <p className="idetail__muted">
          {news.enabled
            ? 'Nessuna notizia recente per questo titolo.'
            : 'Raccolta notizie non attiva: nessuno le sta cercando per questo titolo.'}
        </p>
      )}

      {news && news.items.length > 0 && (
        <ul className="idetail__news">
          {news.items.map((item) => (
            <li key={item.url}>
              <a href={item.url} target="_blank" rel="noopener noreferrer">
                {item.headline}
              </a>
              <span className="idetail__news-meta">
                {item.source ?? 'fonte ignota'}
                {item.publishedAt && <> · {formatDate(item.publishedAt)}</>}
                {item.sentiment !== null && (
                  <>
                    {' · '}
                    <span className={`text-${item.sentiment > 0 ? 'positive' : item.sentiment < 0 ? 'negative' : 'zero'}`}>
                      {/* Attribuito, sempre: è un numero calcolato dalla fonte,
                          non un giudizio della piattaforma. */}
                      sentiment fonte {item.sentiment.toFixed(2)}
                    </span>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {news && !news.enabled && news.items.length > 0 && (
        <p className="idetail__muted">
          La raccolta è spenta: queste notizie sono quelle già scaricate, e non se ne aggiungeranno altre.
        </p>
      )}
    </Card>
  );
}

function MarketStatusLine({ market }: { market: NonNullable<InstrumentDetail['market']> }) {
  const event = nextEventFor({
    status: market.status as MarketStatus,
    nextOpen: market.nextOpen,
    nextClose: market.nextClose,
  });
  return (
    <span className="idetail__market-status">
      <Badge tone={STATUS_TONE[market.status as MarketStatus] ?? 'neutral'}>
        {STATUS_LABELS[market.status as MarketStatus] ?? market.status}
      </Badge>{' '}
      {market.trades24x7 ? 'sempre aperto' : event ? `${event.label.toLowerCase()} ${formatCountdown(event.at)}` : ''}
    </span>
  );
}

function MarketCountdownFacts({ market }: { market: NonNullable<InstrumentDetail['market']> }) {
  if (market.trades24x7) return null;
  const event = nextEventFor({
    status: market.status as MarketStatus,
    nextOpen: market.nextOpen,
    nextClose: market.nextClose,
  });
  if (!event) return null;
  return (
    <>
      <dt>{event.label}</dt>
      <dd className="numeric">{formatCountdown(event.at)}</dd>
    </>
  );
}

function NewAnalysisButton({ instrumentId, enabled }: { instrumentId: string; enabled: boolean }) {
  const request = useRequestAnalysis(instrumentId);
  return (
    <Button
      variant="secondary"
      disabled={!enabled}
      loading={request.isPending}
      title={enabled ? undefined : 'Servizio di analisi AI non disponibile'}
      onClick={() => request.mutate()}
    >
      Nuova analisi AI
    </Button>
  );
}
